import { CommonModule } from '@angular/common';
import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  ViewChild,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ContactInfoFormComponent } from './contact-info-form/contact-info-form.component';
import { ShipmentDetailsFormComponent } from './shipment-details-form/shipment-details-form.component';
import { SnackbarService } from '@loup-transload-ui-app/common-services';
import { RateRequestService } from '../../api/external-web/rate-request.service';

@Component({
  selector: 'app-external-rate-request',
  imports: [
    CommonModule,
    ContactInfoFormComponent,
    ShipmentDetailsFormComponent,
  ],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './external-rate-request.component.html',
  styleUrl: './external-rate-request.component.scss',
})
export class ExternalRateRequestComponent implements OnInit {
  @ViewChild('contactInfoForm') contactInfoForm: ContactInfoFormComponent;
  @ViewChild('shipmentDetailsForm')
  shipmentDetailsForm: ShipmentDetailsFormComponent;

  private rateRequestService = inject(RateRequestService);
  private snackbar = inject(SnackbarService);

  currentStep = 1;
  contactForm: FormGroup;
  shipmentForm: FormGroup;

  constructor(private fb: FormBuilder) {
    this.contactForm = this.fb.group({
      companyName: ['', Validators.required],
      contactName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      email: ['', Validators.required],
    });

    this.shipmentForm = this.fb.group({
      commodityCategory: ['', Validators.required],
      lanes: this.fb.array([this.createLane()]),
      includeRailPricing: [false],
      businessDuration: ['', Validators.required],
      estimatedStartDate: ['', Validators.required],
      comments: [''],
      attachments: [''],
    });
  }

  ngOnInit(): void {
    this.showSuccessMsg();
  }

  goToStep(step: number): void {
    this.currentStep = step;
  }

  submitForm(): void {
    const contactForm = this.contactForm.value;
    const shipmentValue = this.shipmentForm.value;
    const isValid = !shipmentValue.lanes.some(
      (lane) =>
        lane.transloadingServicesRequired.no &&
        lane.truckingServicesRequired.no &&
        !shipmentValue.includeRailPricing
    );
    if (!isValid) {
      const message = `Please select at least one service location as "Yes". All service locations cannot be set to "No".`;
      this.snackbar.show(message, 9000, 'bottom');
      return;
    }
    const isValidCheckbox = !shipmentValue.lanes.some(
      (lane) =>
        (!lane.transloadingServicesRequired.no &&
          !lane.transloadingServicesRequired.origin &&
          !lane.transloadingServicesRequired.destination) ||
        (!lane.truckingServicesRequired.no &&
          !lane.truckingServicesRequired.origin &&
          !lane.truckingServicesRequired.destination) ||
        (!lane.storageServicesRequired.no &&
          !lane.storageServicesRequired.origin &&
          !lane.storageServicesRequired.destination &&
          !lane.transloadingServicesRequired.no)
    );
    if (!isValidCheckbox) {
      const message = `Please select one or more options from the available locations services.`;
      this.snackbar.show(message, 9000, 'bottom');
      return;
    }
    const payload = {
      contact: {
        companyName: contactForm.companyName,
        contactName: contactForm.contactName,
        phoneNumber: contactForm.phoneNumber,
        email: contactForm.email,
      },
      commodityCategory: {
        id: shipmentValue.commodityCategory.id,
        code: shipmentValue.commodityCategory.code,
        type: shipmentValue.commodityCategory.type,
        displayText: shipmentValue.commodityCategory.displayText,
      },
      includeRailPricing: shipmentValue.includeRailPricing,
      businessDuration: {
        id: shipmentValue.businessDuration.id,
        code: shipmentValue.businessDuration.code,
        description: shipmentValue.businessDuration.displayText,
      },
      estimatedStartDate: shipmentValue.estimatedStartDate,
      comments: shipmentValue.comments,
      lanes: shipmentValue.lanes.map((lane: any) => ({
        origin: {
          id: lane.origin.id,
          city: lane.origin.city,
          state: lane.origin.state,
          country: lane.origin.country,
          latitude: lane.origin.latitude,
          longitude: lane.origin.longitude,
        },
        destination: {
          id: lane.destination.id,
          city: lane.destination.city,
          state: lane.destination.state,
          country: lane.destination.country,
          latitude: lane.destination.latitude,
          longitude: lane.destination.longitude,
        },
        transloadingServicesRequired: {
          origin: lane.transloadingServicesRequired.origin,
          destination: lane.transloadingServicesRequired.destination,
        },
        truckingServicesRequired: {
          origin: lane.truckingServicesRequired.origin,
          destination: lane.truckingServicesRequired.destination,
        },
        storageServicesRequired: {
          origin: lane.storageServicesRequired.origin,
          destination: lane.storageServicesRequired.destination,
        },
        estimatedShipmentVolume: {
          units: Number(lane.potentialUnits),
          unitOfMeasure: { id: lane.unitOfMeasure.id },
          timeFrame: { id: lane.timeFrame.id },
        },
      })),
    };
    this.rateRequestService.createRateRequest(payload).subscribe({
      next: (res: any) => {
        if (res.rateRequestId) {
          const message = `Price Quote Request #${res.rateRequestId} has been created successfully.`;
          localStorage.setItem('successMessage', message);
          this.clearAction();
        }
      },
    });
  }

  createLane(): FormGroup {
    return this.fb.group({
      origin: ['', Validators.required],
      destination: ['', Validators.required],
      transloadingServicesRequired: [
        { origin: false, destination: false, no: false },
        Validators.required,
      ],
      truckingServicesRequired: [
        { origin: false, destination: false, no: false },
        Validators.required,
      ],
      storageServicesRequired: [
        { origin: false, destination: false, no: false },
      ],
      potentialUnits: ['', Validators.required],
      unitOfMeasure: ['', Validators.required],
      timeFrame: ['', Validators.required],
    });
  }

  clearAction() {
    window.location.reload();
  }

  showSuccessMsg() {
    const successMessage = localStorage.getItem('successMessage');
    if (successMessage) {
      this.snackbar.show(successMessage, 10000, 'bottom');
      localStorage.removeItem('successMessage');
    }
  }
}
